import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

export interface BookDto {
  id: string;
  projectId: string;
  title: string;
  description?: string | null;
  genre?: string | null;
  pov: string;
  narrativeStyle?: string | null;
  tense: string;
  status: string;
  artStyle?: string | null;
  narratorVoiceId?: string | null;
  label?: string;
  stage?: string | null;
  versionNumber?: number;
  isPrimary?: boolean;
  sourceOutlineId?: string | null;
  chapterCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ChapterDto {
  id: string;
  bookId: string;
  title: string;
  sequenceOrder: number;
  povOverride?: string | null;
  narrativeStyleOverride?: string | null;
  tenseOverride?: string | null;
  ambientAudioUrl?: string | null;
  paragraphCount: number;
  updatedAt: string;
}

export interface ParagraphDto {
  id: string;
  chapterId: string;
  bookId: string;
  contentHtml: string;
  type: string;
  sequenceOrder: number;
  mood?: string | null;
  tone?: string | null;
  emotionalIntensity?: number | null;
  sceneDescription?: string | null;
  speakingEntityIds: string[];
  updatedAt: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  traceId?: string;
}

export function useBooks() {
  const { getToken } = useAuth();
  return useQuery<BookDto[]>({
    queryKey: ['books'],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const response = await client
        .get('api/v2/books?page_size=100')
        .json<ApiResponse<BookDto[]>>();
      return response.data ?? [];
    },
  });
}

export function useBook(bookId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<BookDto>({
    enabled: !!bookId,
    queryKey: ['book', bookId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const response = await client
        .get(`api/v2/books/${bookId}`)
        .json<ApiResponse<BookDto>>();
      return response.data;
    },
  });
}

export function useChapters(bookId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<ChapterDto[]>({
    enabled: !!bookId,
    queryKey: ['chapters', bookId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const response = await client
        .get(`api/v2/books/${bookId}/chapters`)
        .json<ApiResponse<ChapterDto[]>>();
      return response.data ?? [];
    },
  });
}

export function useParagraphs(chapterId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<ParagraphDto[]>({
    enabled: !!chapterId,
    queryKey: ['paragraphs', chapterId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const response = await client
        .get(`api/v2/chapters/${chapterId}/paragraphs`)
        .json<ApiResponse<ParagraphDto[]>>();
      return response.data ?? [];
    },
  });
}

export function useDeleteBook() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/books/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['books'] }),
  });
}

export interface CreateBookInput {
  projectId?: string;
  title: string;
  description?: string;
  genre?: string;
  pov: string;
  narrativeStyle?: string;
  tense: string;
  label?: string;
}

export function useCreateBook() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateBookInput) => {
      const client = createApiClient(getToken);
      const response = await client
        .post('api/v2/books', { json: input })
        .json<ApiResponse<BookDto>>();
      return { data: response.data, projectId: input.projectId };
    },
    onSuccess: ({ projectId }) => {
      void queryClient.invalidateQueries({ queryKey: ['books'] });
      if (projectId) {
        void queryClient.invalidateQueries({
          queryKey: ['project', projectId],
        });
      }
    },
  });
}

export function useCreateChapter(bookId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (title: string) => {
      if (!bookId) throw new Error('bookId required');
      const client = createApiClient(getToken);
      const response = await client
        .post(`api/v2/books/${bookId}/chapters`, { json: { title } })
        .json<ApiResponse<ChapterDto>>();
      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['chapters', bookId] }),
  });
}

export interface UpdateParagraphInput {
  id: string;
  contentHtml: string;
  type?: string;
  mood?: string | null;
  tone?: string | null;
  emotionalIntensity?: number | null;
  sceneDescription?: string | null;
  speakingEntityIds?: string[];
}

export function useUpdateParagraph() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateParagraphInput) => {
      const client = createApiClient(getToken);
      const response = await client
        .put(`api/v2/paragraphs/${input.id}`, {
          json: {
            contentHtml: input.contentHtml,
            type: input.type ?? 'prose',
            mood: input.mood,
            tone: input.tone,
            emotionalIntensity: input.emotionalIntensity,
            sceneDescription: input.sceneDescription,
            speakingEntityIds: input.speakingEntityIds,
          },
        })
        .json<ApiResponse<ParagraphDto>>();
      return response.data;
    },
    onSuccess: (data) =>
      queryClient.invalidateQueries({
        queryKey: ['paragraphs', data.chapterId],
      }),
  });
}

// --- Project-scoped book mutations ---

export function useDuplicateBook() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      bookId: string;
      projectId: string;
      newLabel?: string;
    }) => {
      const client = createApiClient(getToken);
      const response = await client
        .post(`api/v2/books/${input.bookId}/duplicate`, {
          json: { newLabel: input.newLabel },
        })
        .json<ApiResponse<BookDto>>();
      return { data: response.data, projectId: input.projectId };
    },
    onSuccess: ({ projectId }) => {
      void queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['books'] });
    },
  });
}

export function useSetPrimaryBook() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { bookId: string; projectId: string }) => {
      const client = createApiClient(getToken);
      await client.post(`api/v2/books/${input.bookId}/set-primary`);
      return input;
    },
    onSuccess: ({ projectId }) => {
      void queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
}

export function useRenameBook() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      bookId: string;
      projectId: string;
      label: string;
      stage?: string;
    }) => {
      const client = createApiClient(getToken);
      await client.patch(`api/v2/books/${input.bookId}/label`, {
        json: { label: input.label, stage: input.stage },
      });
      return input;
    },
    onSuccess: ({ projectId, bookId }) => {
      void queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['book', bookId] });
    },
  });
}
